import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Clock, AlertCircle, Home } from "lucide-react";
import Header from "../common/header";
import Footer from "../common/footer";
import { all_routes } from "../router/all_routes";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("");
  const [TransactionID, setTransactionID] = useState("");

  useEffect(() => {
    if (!status && !TransactionID) {
      const paymentStatus = searchParams.get("status");
      const paymentTransactionID = searchParams.get("TransactionID");

      setStatus(paymentStatus ?? "");
      setTransactionID(paymentTransactionID ?? "");

      const cleanURL = window.location.pathname;
      window.history.replaceState(null, "", cleanURL);
    }
  }, [searchParams, status, TransactionID]);

  const getStatusConfig = () => {
    switch (status) {
      case "Completed":
        return {
          icon: CheckCircle,
          title: "Payment Successful!",
          message:
            "Your payment has been processed successfully. Thank you for your booking!",
          badgeVariant: "default" as const,
          badgeText: "Completed",
          iconColor: "text-green-500",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
        };
      case "Failed":
        return {
          icon: XCircle,
          title: "Payment Failed",
          message:
            "We couldn't process your payment. Please try again or use a different payment method.",
          badgeVariant: "destructive" as const,
          badgeText: "Failed",
          iconColor: "text-red-500",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };
      case "Pending":
        return {
          icon: Clock,
          title: "Payment Canceled",
          message:
            "Your payment was canceled. You can complete the payment anytime from your bookings.",
          badgeVariant: "secondary" as const,
          badgeText: "Canceled",
          iconColor: "text-yellow-500",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
        };
      default:
        return {
          icon: AlertCircle,
          title: "Unknown Status",
          message:
            "We couldn't determine the status of your payment. Please contact support.",
          badgeVariant: "outline" as const,
          badgeText: "Unknown",
          iconColor: "text-gray-500",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
        };
    }
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 md:pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card
            className={`shadow-lg border-0 ${config.bgColor} ${config.borderColor} overflow-hidden`}
          >
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <div
                  className={`p-3 rounded-full ${config.bgColor} border-4 ${config.borderColor}`}
                >
                  <IconComponent className={`w-12 h-12 ${config.iconColor}`} />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                {config.title}
              </CardTitle>
              <Badge
                variant={config.badgeVariant}
                className="text-sm font-medium px-3 py-1"
              >
                {config.badgeText}
              </Badge>
            </CardHeader>

            <CardContent className="text-center space-y-6">
              <p className="text-gray-600 text-lg leading-relaxed">
                {config.message}
              </p>

              {TransactionID && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <p className="text-sm text-gray-500 font-medium mb-1">
                    Transaction ID
                  </p>
                  <p className="text-lg font-mono font-semibold text-gray-800 break-all">
                    {TransactionID}
                  </p>
                </div>
              )}

              <div className="flex justify-center pt-4">
                <Button asChild size="lg" className="gap-2">
                  <Link to={all_routes.homeOne}>
                    <Home className="w-4 h-4" />
                    Back to Home
                  </Link>
                </Button>
              </div>

              {status === "Completed" && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    A confirmation email has been sent to your registered email
                    address.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Help Section */}
          {(status === "Failed" || status === "Pending") && (
            <Card className="mt-6 shadow-sm border-gray-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Need Help?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Contact our support team for assistance with your payment.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button variant="outline" size="sm">
                      <Link to={all_routes.contactUs}>Contact Support</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentSuccess;
